import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheck, Download, EyeOff, FileText, ChevronDown, ChevronRight, FileSignature, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONSENTS = [
  { key: 'operativo', label: 'Gestione rapporto/servizio e obblighi di legge', required: true },
  { key: 'marketing', label: 'Comunicazioni informative e commerciali (newsletter) — = "News e novità" nell\'app' },
  { key: 'profilazione', label: 'Profilazione per offerte personalizzate' },
];

const dmy = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString('it-IT');
};

export default function ClientPrivacyPanel({ client, onUpdated }) {
  const { isSuperAdmin, isMaster } = useAuth();
  const canAnonymize = (typeof isSuperAdmin === 'function' ? isSuperAdmin() : false)
    || (typeof isMaster === 'function' ? isMaster() : false);

  const [consents, setConsents] = useState({
    operativo: !!client?.consents?.operativo,
    marketing: !!client?.consents?.marketing,
    profilazione: !!client?.consents?.profilazione,
  });
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showInformativa, setShowInformativa] = useState(false);
  const [informativa, setInformativa] = useState(null);

  const meta = client?.consents_meta || {};
  const history = client?.consents_history || [];

  const loadInformativa = async () => {
    if (!showInformativa && !informativa) {
      try {
        const r = await axios.get(`${API}/clients/informativa`);
        setInformativa(r.data);
      } catch (_) { toast.error('Informativa non disponibile'); }
    }
    setShowInformativa((v) => !v);
  };

  const toggle = async (key, val) => {
    if (key === 'operativo' && !val) {
      toast.error('Il consenso operativo è necessario per la gestione del rapporto');
      return;
    }
    const next = { ...consents, [key]: val };
    setConsents(next);
    setSaving(true);
    try {
      const res = await axios.put(`${API}/clients/${client.id}/consents`, next);
      toast.success('Consensi aggiornati');
      onUpdated && onUpdated(res.data);
    } catch (e) {
      setConsents(consents); // rollback
      toast.error(e.response?.data?.detail || 'Errore aggiornamento consensi');
    } finally {
      setSaving(false);
    }
  };

  const exportData = async () => {
    try {
      const res = await axios.get(`${API}/clients/${client.id}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_${client.client_code || client.id}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Dati esportati');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore export');
    }
  };

  const downloadModulo = async () => {
    try {
      const res = await axios.get(`${API}/clients/${client.id}/consensi-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `consensi_${client.client_code || client.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione modulo');
    }
  };

  const sendRequest = async () => {
    if (!client.email) {
      toast.error('Il cliente non ha un indirizzo email');
      return;
    }
    if (!window.confirm(`Inviare a ${client.email} il link per scegliere i consensi privacy?`)) return;
    try {
      const res = await axios.post(`${API}/clients/${client.id}/consent-request`);
      if (res.data.sent) {
        toast.success(`Email inviata a ${res.data.email}`);
      } else {
        toast.error(`Email non inviata: ${res.data.error || 'errore SMTP'}`);
        window.prompt('Link da inviare manualmente al cliente:', res.data.link);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio richiesta');
    }
  };

  const anonymize = async () => {
    if (!window.confirm(
      "ANONIMIZZAZIONE (diritto all'oblio)\n\n" +
      "Verranno rimossi in modo permanente nome, codice fiscale, contatti, indirizzo e " +
      "documenti di questo cliente. I record collegati (polizze, fatture, ecc.) restano " +
      "per gli obblighi di legge ma senza dati identificativi.\n\nL'operazione è IRREVERSIBILE. Procedere?"
    )) return;
    try {
      await axios.post(`${API}/clients/${client.id}/anonymize`);
      toast.success('Cliente anonimizzato');
      onUpdated && onUpdated({ ...client, is_anonymized: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'anonimizzazione");
    }
  };

  if (client?.is_anonymized) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
        <div className="flex items-center gap-2 text-slate-700 font-medium">
          <EyeOff className="w-4 h-4" /> Cliente anonimizzato
        </div>
        <p className="text-slate-500 mt-1">
          I dati identificativi sono stati rimossi (diritto all'oblio, Art. 17).
          {client.anonymized_at ? ` Il ${dmy(client.anonymized_at)}.` : ''}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-slate-900 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" /> Consensi Privacy
        </h4>
        <button onClick={loadInformativa} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
          <FileText className="w-3.5 h-3.5" /> Informativa {meta.informativa_version ? `v${meta.informativa_version}` : ''}
        </button>
      </div>

      {showInformativa && informativa && (
        <pre className="text-[11px] leading-relaxed bg-slate-50 border rounded p-3 max-h-52 overflow-y-auto whitespace-pre-wrap text-slate-600">
          {informativa.text}
        </pre>
      )}

      <div className="space-y-2">
        {CONSENTS.map((c) => (
          <div key={c.key} className="flex items-center justify-between gap-3">
            <span className="text-sm text-slate-700">
              {c.label} {c.required && <Badge className="bg-slate-100 text-slate-500 text-[10px] ml-1">necessario</Badge>}
            </span>
            <Switch checked={consents[c.key]} disabled={saving} onCheckedChange={(v) => toggle(c.key, v)} />
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-400">
        {meta.updated_at ? `Ultima modifica: ${dmy(meta.updated_at)}${meta.updated_by === 'app_cliente' ? " (dall'app del cliente)" : ''}` : 'Consensi non ancora registrati'}
        {history.length > 0 && (
          <button onClick={() => setShowHistory((v) => !v)} className="ml-2 text-blue-600 hover:underline inline-flex items-center">
            {showHistory ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} storico ({history.length})
          </button>
        )}
      </p>
      {showHistory && (
        <div className="text-xs text-slate-500 border-t pt-2 space-y-1 max-h-40 overflow-y-auto">
          {[...history].reverse().map((h, i) => (
            <div key={i}>
              <span className="text-slate-400">{dmy(h.at)}</span> · {h.origine === 'app' ? <b className="text-emerald-700">dall'app del cliente</b> : `v${h.informativa_version}`} ·{' '}
              {CONSENTS.filter((c) => h.consents?.[c.key]).map((c) => c.key).join(', ') || 'nessuno'}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t">
        <Button size="sm" variant="outline" onClick={downloadModulo}>
          <FileSignature className="w-4 h-4 mr-1" /> Modulo da firmare (PDF)
        </Button>
        <Button size="sm" variant="outline" onClick={sendRequest} disabled={!client.email} title={client.email ? '' : 'Il cliente non ha email'}>
          <Mail className="w-4 h-4 mr-1" /> Richiedi consensi via email
        </Button>
        <Button size="sm" variant="outline" onClick={exportData}>
          <Download className="w-4 h-4 mr-1" /> Esporta dati (JSON)
        </Button>
        {canAnonymize && (
          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={anonymize}>
            <EyeOff className="w-4 h-4 mr-1" /> Anonimizza (oblio)
          </Button>
        )}
      </div>
    </div>
  );
}
